import React from "react";
import { Box, Button, HStack, IconButton, Text, Tooltip, useClipboard, useColorModeValue, useToast } from "@chakra-ui/react";
import { FaCheck, FaFacebook, FaInstagram, FaLink, FaLinkedin, FaTwitter } from "react-icons/fa";

interface ShareFollowFooterProps {
  shareUrl: string;
  orgName?: string;
  socialLinks?: {
    facebook?: string;
    linkedin?: string;
    twitter?: string;
    instagram?: string;
  };
}

export const ShareFollowFooter: React.FC<ShareFollowFooterProps> = ({
  shareUrl,
  orgName,
  socialLinks,
}) => {
  const cardBg = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const labelColor = useColorModeValue("gray.500", "gray.400");
  const { hasCopied, onCopy } = useClipboard(shareUrl);
  const toast = useToast();

  const handleCopy = () => {
    onCopy();
    toast({
      title: "Link copied!",
      status: "success",
      duration: 2000,
      isClosable: true,
    });
  };

  const socials = [
    { key: "facebook", label: "Facebook", icon: <FaFacebook />, url: socialLinks?.facebook },
    { key: "linkedin", label: "LinkedIn", icon: <FaLinkedin />, url: socialLinks?.linkedin },
    { key: "twitter", label: "Twitter", icon: <FaTwitter />, url: socialLinks?.twitter },
    { key: "instagram", label: "Instagram", icon: <FaInstagram />, url: socialLinks?.instagram },
  ].filter((s) => s.url);

  return (
    <Box bg={cardBg} borderWidth={1} borderColor={borderColor} rounded="xl" shadow="sm" p={{ base: 4, md: 5 }} w="100%">
      <Text fontSize="xs" fontWeight="semibold" color={labelColor} textTransform="uppercase" letterSpacing="wide" mb={2}>
        Share
      </Text>
      <Button
        w="100%"
        variant="outline"
        leftIcon={hasCopied ? <FaCheck /> : <FaLink />}
        onClick={handleCopy}
      >
        {hasCopied ? "Copied!" : "Copy certificate link"}
      </Button>

      {socials.length > 0 && (
        <>
          <Text fontSize="xs" fontWeight="semibold" color={labelColor} textTransform="uppercase" letterSpacing="wide" mt={5} mb={2}>
            Follow {orgName || "us"}
          </Text>
          <HStack spacing={2}>
            {socials.map((s) => (
              <Tooltip key={s.key} label={`Follow on ${s.label}`}>
                <IconButton
                  aria-label={`Follow on ${s.label}`}
                  icon={s.icon}
                  variant="outline"
                  rounded="full"
                  onClick={() => window.open(s.url, "_blank")}
                />
              </Tooltip>
            ))}
          </HStack>
        </>
      )}
    </Box>
  );
};
